// ============================================
// Earth Shaders
// Fragment and vertex shaders for Earth rendering
// ============================================

import { earthShaderFunctions } from './common';

/**
 * Earth surface vertex shader
 */
export const earthVertexShader = `
    varying vec2 vUv;
    varying vec3 vNormal;
    varying vec3 vPosition;

    void main() {
        vUv = uv;
        vNormal = normalize(normalMatrix * normal);
        vPosition = position;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
`;

/**
 * Earth surface fragment shader - oceans and continents
 */
export const earthFragmentShader = `
    varying vec2 vUv;
    varying vec3 vNormal;
    varying vec3 vPosition;
    uniform float time;
    uniform vec3 sunDirection;

    ${earthShaderFunctions}

    // Continent shape function
    float continent(vec2 uv, vec2 center, vec2 size, float edgeNoise) {
        vec2 d = (uv - center) / size;
        float dist = length(d);
        float n = noise(uv * 80.0) * edgeNoise;
        return smoothstep(1.0 + n, 0.5 + n, dist);
    }

    float island(vec2 uv, vec2 center, float size) {
        float dist = length(uv - center);
        float n = noise(uv * 100.0) * 0.3;
        return smoothstep(size + n * size, size * 0.2, dist);
    }

    void main() {
        vec2 uv = vUv;
        uv.x = fract(uv.x + 0.375);

        // Fixed view direction (always toward camera, Z-axis positive)
        vec3 viewDir = vec3(0.0, 0.0, 1.0);
        vec3 normal = normalize(vNormal);

        // Fresnel coefficient (basis for sphere effect)
        float fresnel = 1.0 - max(0.0, dot(normal, viewDir));
        float fresnelPow = pow(fresnel, 1.5);

        // Sun light calculation (adjusted brighter)
        float sunLight = max(0.0, dot(normal, sunDirection));
        float ambient = 0.35;  // Bright
        float diffuse = sunLight * 0.65;
        float lighting = ambient + diffuse;

        // ===== Ocean (vivid blue) =====
        vec3 oceanDeep = vec3(0.05, 0.18, 0.45);
        vec3 oceanMid = vec3(0.08, 0.30, 0.58);
        vec3 oceanShallow = vec3(0.15, 0.45, 0.72);

        float oceanVariation = fbm(uv * 12.0, 5);
        vec3 ocean = mix(oceanDeep, oceanMid, oceanVariation);
        ocean = mix(ocean, oceanShallow, noise(uv * 30.0) * 0.4);

        // Ocean specular highlight (sun reflection)
        vec3 halfDir = normalize(sunDirection + viewDir);
        float specular = pow(max(0.0, dot(normal, halfDir)), 40.0) * sunLight;
        ocean += vec3(0.3, 0.5, 0.7) * specular * 0.5;

        // ===== Continents =====
        float land = 0.0;

        // Japan
        land = max(land, continent(uv, vec2(0.500, 0.695), vec2(0.020, 0.055), 0.15));
        land = max(land, continent(uv, vec2(0.505, 0.742), vec2(0.014, 0.022), 0.12));
        land = max(land, continent(uv, vec2(0.495, 0.660), vec2(0.008, 0.012), 0.1));
        land = max(land, continent(uv, vec2(0.490, 0.655), vec2(0.010, 0.016), 0.1));

        // Korean Peninsula
        land = max(land, continent(uv, vec2(0.470, 0.700), vec2(0.014, 0.042), 0.12));

        // China
        land = max(land, continent(uv, vec2(0.420, 0.680), vec2(0.075, 0.095), 0.18));
        land = max(land, continent(uv, vec2(0.380, 0.720), vec2(0.040, 0.050), 0.15));

        // Taiwan
        land = max(land, continent(uv, vec2(0.502, 0.618), vec2(0.010, 0.018), 0.08));

        // Southeast Asia
        land = max(land, continent(uv, vec2(0.400, 0.575), vec2(0.028, 0.065), 0.14));
        land = max(land, continent(uv, vec2(0.405, 0.520), vec2(0.012, 0.035), 0.10));

        // Philippines
        land = max(land, island(uv, vec2(0.512, 0.580), 0.008));
        land = max(land, island(uv, vec2(0.515, 0.555), 0.010));
        land = max(land, island(uv, vec2(0.510, 0.530), 0.007));

        // Indonesia
        land = max(land, continent(uv, vec2(0.450, 0.500), vec2(0.028, 0.032), 0.12));
        land = max(land, continent(uv, vec2(0.400, 0.485), vec2(0.038, 0.014), 0.10));
        land = max(land, continent(uv, vec2(0.432, 0.458), vec2(0.028, 0.008), 0.08));
        land = max(land, island(uv, vec2(0.468, 0.485), 0.012));

        // Oceania
        land = max(land, continent(uv, vec2(0.550, 0.480), vec2(0.038, 0.018), 0.12));
        land = max(land, continent(uv, vec2(0.530, 0.350), vec2(0.075, 0.055), 0.15));
        land = max(land, island(uv, vec2(0.600, 0.280), 0.008));
        land = max(land, island(uv, vec2(0.605, 0.260), 0.006));

        // India
        land = max(land, continent(uv, vec2(0.320, 0.580), vec2(0.038, 0.065), 0.14));
        land = max(land, continent(uv, vec2(0.300, 0.630), vec2(0.025, 0.030), 0.10));
        land = max(land, island(uv, vec2(0.335, 0.535), 0.008));

        // Siberia
        land = max(land, continent(uv, vec2(0.450, 0.820), vec2(0.110, 0.075), 0.20));
        land = max(land, continent(uv, vec2(0.520, 0.780), vec2(0.030, 0.025), 0.12));

        // ===== Land colors (stylized, bright) =====
        vec3 forest = vec3(0.22, 0.50, 0.28);
        vec3 grassland = vec3(0.40, 0.58, 0.35);
        vec3 desert = vec3(0.78, 0.68, 0.50);
        vec3 mountain = vec3(0.55, 0.50, 0.45);
        vec3 snow = vec3(0.95, 0.97, 1.0);

        // Climate zones by latitude
        float latitude = abs(uv.y - 0.5) * 2.0;

        // Terrain noise
        float terrain = fbm(uv * 25.0, 5);
        float elevation = fbm(uv * 40.0, 4);

        // Biome blending (simple)
        vec3 landColor = forest;
        landColor = mix(landColor, grassland, smoothstep(0.4, 0.6, terrain));
        landColor = mix(landColor, desert, smoothstep(0.55, 0.75, terrain) * (1.0 - latitude) * 0.6);
        landColor = mix(landColor, mountain, smoothstep(0.65, 0.85, elevation) * 0.5);
        landColor = mix(landColor, snow, smoothstep(0.78, 0.92, latitude));

        // Combine ocean and land
        vec3 surfaceColor = mix(ocean, landColor, land);

        // ===== Apply lighting =====
        surfaceColor *= lighting;

        // ===== Blue glowing atmosphere effect (enhanced) =====
        vec3 atmosphereBlue = vec3(0.45, 0.75, 1.0);

        // Atmospheric scattering at edges (stronger)
        float rimLight = pow(fresnel, 1.8) * 0.75;
        surfaceColor = mix(surfaceColor, atmosphereBlue, rimLight);

        // Add blue tint overall (Earth-like, enhanced)
        surfaceColor += atmosphereBlue * pow(fresnel, 2.5) * 0.38;

        // Night side (light)
        float nightSide = 1.0 - smoothstep(0.0, 0.2, sunLight);
        surfaceColor *= mix(1.0, 0.25, nightSide);

        // Terminator glow (enhanced)
        float terminator = smoothstep(0.0, 0.25, sunLight) * smoothstep(0.35, 0.15, sunLight);
        surfaceColor += atmosphereBlue * terminator * fresnel * 0.28;

        gl_FragColor = vec4(surfaceColor, 1.0);
    }
`;

/**
 * Cloud layer vertex shader
 */
export const cloudVertexShader = `
    varying vec2 vUv;
    varying vec3 vNormal;

    void main() {
        vUv = uv;
        vNormal = normalize(normalMatrix * normal);
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
`;

/**
 * Cloud layer fragment shader
 */
export const cloudFragmentShader = `
    varying vec2 vUv;
    varying vec3 vNormal;
    uniform float time;
    uniform vec3 sunDirection;

    ${earthShaderFunctions}

    void main() {
        vec2 uv = vUv;
        // Fixed view direction
        vec3 viewDir = vec3(0.0, 0.0, 1.0);
        vec3 normal = normalize(vNormal);

        // Fresnel (edge transparency)
        float fresnel = 1.0 - max(0.0, dot(normal, viewDir));
        float sphereFactor = pow(1.0 - fresnel, 0.6);

        // Cloud UV (moves with time)
        vec2 cloudUv = uv;
        cloudUv.x = fract(cloudUv.x + time * 0.002);
        cloudUv.y += sin(cloudUv.x * 6.28 + time * 0.01) * 0.003;

        // ===== Weather patterns (subtle) =====
        // Typhoon (spiral) - kept as a feature
        vec2 typhoonCenter = vec2(0.52, 0.58);
        float typhoonDist = length(uv - typhoonCenter);
        float typhoonAngle = atan(uv.y - typhoonCenter.y, uv.x - typhoonCenter.x);
        float spiral = sin(typhoonAngle * 3.0 - typhoonDist * 18.0 + time * 0.25) * 0.5 + 0.5;
        float typhoonArea = smoothstep(0.10, 0.03, typhoonDist) * 0.7;

        // Frontal system (thin)
        float frontLat = 0.65 + sin(uv.x * 10.0 + time * 0.04) * 0.02;
        float frontArea = smoothstep(0.06, 0.01, abs(uv.y - frontLat)) * 0.4;

        // Intertropical Convergence Zone (thin)
        float itczLat = 0.48 + sin(uv.x * 6.0) * 0.015;
        float tropicalArea = smoothstep(0.05, 0.01, abs(uv.y - itczLat)) * 0.35;

        // Polar low pressure (small)
        float polarArea = smoothstep(0.08, 0.02, length(uv - vec2(0.38, 0.82))) * 0.5;
        polarArea = max(polarArea, smoothstep(0.07, 0.02, length(uv - vec2(0.58, 0.84))) * 0.4);

        // Scattered cumulus (quite thin)
        float scatteredBase = fbm(cloudUv * 5.0 + time * 0.008, 4);
        float scattered = smoothstep(0.60, 0.80, scatteredBase) * 0.3;

        // Combine cloud areas (overall thin)
        float cloudArea = 0.0;
        cloudArea = max(cloudArea, typhoonArea);
        cloudArea = max(cloudArea, frontArea);
        cloudArea = max(cloudArea, tropicalArea);
        cloudArea = max(cloudArea, polarArea);
        cloudArea = max(cloudArea, scattered);

        // ===== Cloud detail =====
        float cloudDetail = fbm(cloudUv * 18.0, 5);

        // Typhoon spiral structure
        float typhoonDetail = mix(cloudDetail, spiral * cloudDetail * 1.3, typhoonArea);

        // Final cloud density (thin)
        float cloudDensity = cloudArea * (0.3 + typhoonDetail * 0.5);
        cloudDensity = smoothstep(0.12, 0.40, cloudDensity);

        // Edge attenuation on sphere (strong)
        cloudDensity *= sphereFactor;

        // Ensure no clouds in cloudless areas
        cloudDensity *= step(0.08, cloudArea);

        // ===== Cloud color (bright, soft) =====
        float sunLight = max(0.0, dot(normal, sunDirection));

        // Cloud base color (bright)
        vec3 cloudBright = vec3(1.0, 1.0, 1.0);
        vec3 cloudMid = vec3(0.95, 0.96, 0.98);

        // Color variation with thickness and light (subtle contrast)
        float thickness = cloudDensity * cloudDetail;
        vec3 cloudColor = mix(cloudMid, cloudBright, smoothstep(0.2, 0.6, thickness));

        // Sun shadow (light)
        cloudColor *= mix(0.75, 1.0, sunLight);

        // Night clouds (light)
        float nightSide = 1.0 - smoothstep(0.0, 0.2, sunLight);
        cloudColor *= mix(1.0, 0.4, nightSide);

        // Cloud edge glow (soft)
        float rimCloud = pow(fresnel, 2.5) * sunLight;
        cloudColor += vec3(0.3, 0.4, 0.5) * rimCloud * cloudDensity * 0.5;

        // Transparency (quite thin)
        float alpha = cloudDensity * 0.55;

        gl_FragColor = vec4(cloudColor, alpha);
    }
`;

/**
 * Atmosphere layer vertex shader
 */
export const atmosphereVertexShader = `
    varying vec3 vNormal;

    void main() {
        vNormal = normalize(normalMatrix * normal);
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
`;

/**
 * Atmosphere layer fragment shader
 */
export const atmosphereFragmentShader = `
    varying vec3 vNormal;
    uniform vec3 sunDirection;

    void main() {
        // Fixed view direction
        vec3 viewDir = vec3(0.0, 0.0, 1.0);
        vec3 normal = normalize(vNormal);

        // Fresnel (0=Earth center, 1=edge toward space)
        float fresnel = 1.0 - abs(dot(normal, viewDir));

        // Transparency toward outer edge (Earth side dense, space side thin)
        float glow = 1.0 - fresnel;

        // Slightly brighter on sun side
        float sunInfluence = max(0.0, dot(normal, sunDirection)) * 0.15 + 0.85;

        // Atmosphere color
        vec3 atmosphereColor = vec3(0.4, 0.72, 1.0);

        // Alpha
        float alpha = glow * 0.5 * sunInfluence;

        gl_FragColor = vec4(atmosphereColor, alpha);
    }
`;
